import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { getVapidPublicKey, savePushSubscription, listMyPushDevices } from "@/lib/push.functions";

const DISMISS_KEY = "bidnova.push-banner.dismissed";

function isPushCapable(): boolean {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function PushEnableBanner() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const vapidFn = useServerFn(getVapidPublicKey);
  const saveSub = useServerFn(savePushSubscription);
  const listDevices = useServerFn(listMyPushDevices);

  const [permission, setPermission] = useState<NotificationPermission | "unsupported">("default");
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const devicesQ = useQuery({
    queryKey: ["push-devices"],
    queryFn: () => listDevices(),
    enabled: !!user && isPushCapable(),
  });
  const hasDbSub = (devicesQ.data ?? []).some((d) => !d.disabled_at);

  useEffect(() => {
    if (!isPushCapable()) { setPermission("unsupported"); return; }
    setPermission(Notification.permission);
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === "1"); } catch { /* noop */ }
  }, []);

  const enable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setSuccess(false);
    setStatus("Enabling push notifications…");
    try {
      // 3. Check browser support
      if (!isPushCapable()) throw new Error("Push notifications are not supported in this browser.");

      // 4. Register or retrieve service worker
      setStatus("Registering service worker…");
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });

      // 5. Wait for ready
      setStatus("Waiting for service worker…");
      const ready = await navigator.serviceWorker.ready;

      // Request permission (no-op if already granted)
      setStatus("Requesting notification permission…");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error(perm === "denied"
          ? "Notifications are blocked. Enable them in your browser settings."
          : "Notification permission was not granted.");
      }

      // 6. Existing subscription?
      setStatus("Checking existing subscription…");
      let sub = await ready.pushManager.getSubscription();

      // 7. No sub — fetch VAPID and subscribe
      if (!sub) {
        setStatus("Loading VAPID key…");
        const { key } = await vapidFn();
        if (!key) throw new Error("Server VAPID key is not configured.");
        const appServerKey = urlBase64ToUint8Array(key);
        setStatus("Subscribing with push service…");
        sub = await ready.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey.buffer.slice(
            appServerKey.byteOffset,
            appServerKey.byteOffset + appServerKey.byteLength,
          ) as ArrayBuffer,
        });
      }

      if (!sub) throw new Error("Failed to obtain a push subscription.");

      // 8. Save to DB (upsert)
      setStatus("Saving subscription to server…");
      const p256dh = bufToBase64Url(sub.getKey("p256dh"));
      const auth = bufToBase64Url(sub.getKey("auth"));
      if (!sub.endpoint || !p256dh || !auth) throw new Error("Subscription is missing required keys.");
      await saveSub({ data: {
        endpoint: sub.endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
      }});

      // 10. Success
      setStatus("Push notifications enabled");
      setSuccess(true);
      qc.invalidateQueries({ queryKey: ["push-devices"] });
      qc.invalidateQueries({ queryKey: ["admin-push-subs"] });
      qc.invalidateQueries({ queryKey: ["admin-push-stats"] });
      qc.invalidateQueries({ queryKey: ["push-debug"] });
    } catch (e) {
      const msg = (e as Error).message || String(e);
      console.error("[push] enable failed:", e);
      setError(msg);
      setStatus(null);
    } finally {
      setBusy(false);
    }
  }, [busy, qc, saveSub, vapidFn]);

  function dismiss() {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  }

  if (loading || !user) return null;
  if (permission === "unsupported") return null;
  // Show banner when there is no active DB subscription (regardless of browser permission state,
  // except when explicitly dismissed and no error is showing).
  if (hasDbSub && !error && !success) return null;
  if (dismissed && permission === "default") return null;

  return (
    <div className="sticky top-0 z-40 border-b border-border/60 bg-surface-2/95 backdrop-blur">
      <div className="container flex flex-wrap items-center gap-3 py-2 text-sm">
        {error ? (
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
        ) : success ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
        ) : (
          <Bell className="h-4 w-4 text-gold shrink-0" />
        )}
        <span className="flex-1 min-w-[200px] truncate">
          {error
            ? `Push error: ${error}`
            : status
            ? status
            : success
            ? "Push notifications enabled on this device."
            : "Get instant alerts for outbids, ending auctions and wins."}
        </span>
        {!success && (
          <Button size="sm" onClick={enable} disabled={busy}>
            {busy ? (<><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Enabling…</>) : "Enable Push Notifications"}
          </Button>
        )}
        <button onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
